import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Modal,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Typography,
  Chip,
  IconButton,
  Paper,
  Fade,
  Backdrop,
} from '@mui/material';
import {
  Store as StoreIcon,
  Search as SearchIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useAppStore } from '../../store/appStore';
import api from '../../services/api';

const ALL_OUTLETS_OPTION = {
  id: 'all-outlets',
  name: 'All Outlets',
  address: 'View data from all branches',
  city: '',
  state: '',
  isAllOutlets: true,
};

const BranchSelector = () => {
  const { branch: selectedBranch, setBranch } = useAppStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [branches, setBranches] = useState([]);
  const [filteredBranches, setFilteredBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Initialize with All Outlets as default on mount
  useEffect(() => {
    const hasSelectedBranch = selectedBranch !== null && selectedBranch !== undefined;
    if (!hasSelectedBranch) {
      setBranch(ALL_OUTLETS_OPTION);
    }
  }, []);

  const fetchBranches = async () => {
    try {
      setLoading(true);
      const response = await api.get('/vendors/branches/');
      if (response.data.success) {
        const branchesData = response.data.data;
        setBranches(branchesData);
        // Always show All Outlets first, followed by actual branches
        setFilteredBranches([ALL_OUTLETS_OPTION, ...branchesData]);
      } else {
        console.error('Failed to fetch branches:', response.data);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = useCallback((event) => {
    const value = event.target.value;
    setSearchTerm(value);

    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Set new timeout for debounced search
    const timeout = setTimeout(() => {
      if (value.trim() === '') {
        // Always include All Outlets at the top when no search
        setFilteredBranches([ALL_OUTLETS_OPTION, ...branches]);
      } else {
        const searchLower = value.toLowerCase();
        // Filter All Outlets and branches based on search
        const allOutletsMatches = ALL_OUTLETS_OPTION.name.toLowerCase().includes(searchLower);
        const filtered = branches.filter(branch =>
          branch.name.toLowerCase().includes(searchLower) ||
          branch.address.toLowerCase().includes(searchLower) ||
          branch.city?.toLowerCase().includes(searchLower) ||
          branch.state?.toLowerCase().includes(searchLower)
        );
        
        if (allOutletsMatches) {
          setFilteredBranches([ALL_OUTLETS_OPTION, ...filtered]);
        } else {
          setFilteredBranches(filtered);
        }
      }
    }, 300);

    setSearchTimeout(timeout);
  }, [branches, searchTimeout]);

  const handleBranchSelect = (branch) => {
    setBranch(branch);
    setIsModalOpen(false);
    setSearchTerm('');
  };

  const handleModalOpen = () => {
    setIsModalOpen(true);
    fetchBranches();
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSearchTerm('');
    setFilteredBranches(branches);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  return (
    <>
      {/* Branch Selector Button */}
      <Button
        onClick={handleModalOpen}
        startIcon={<StoreIcon />}
        variant="outlined"
        sx={{
          minWidth: 'auto',
          px: 2,
          py: 1,
          borderRadius: 2,
          borderColor: 'divider',
          color: 'text.primary',
          backgroundColor: 'background.paper',
          '&:hover': {
            backgroundColor: 'action.hover',
            borderColor: 'primary.main',
          },
          textTransform: 'none',
          maxWidth: { xs: 120, sm: 200 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {selectedBranch?.name || 'Select Branch'}
          </Typography>
          {selectedBranch && !selectedBranch.isAllOutlets && (
            <Chip
              label="Active"
              size="small"
              color="success"
              variant="outlined"
              sx={{ fontSize: '0.7rem', height: 18 }}
            />
          )}
          {selectedBranch?.isAllOutlets && (
            <Chip
              label="All"
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontSize: '0.7rem', height: 18 }}
            />
          )}
        </Box>
      </Button>

      {/* Branch Selection Modal */}
      <Modal
        open={isModalOpen}
        onClose={handleModalClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={isModalOpen}>
          <Paper
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: 'calc(100% - 24px)', sm: 'min(680px, calc(100vw - 32px))', md: 680 },
              maxWidth: 'calc(100vw - 24px)',
              maxHeight: 'calc(100vh - 40px)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              borderRadius: { xs: 2, sm: 3 },
              boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
              m: 0,
            }}
          >
            {/* Header */}
            <Box
              sx={{
                px: { xs: 2, sm: 3 },
                py: { xs: 2, sm: 2.5 },
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexShrink: 0,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <BusinessIcon color="primary" />
                Select Branch
              </Typography>
              <IconButton onClick={handleModalClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Search */}
            <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2.25 }, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
              <TextField
                fullWidth
                placeholder="Search branches by name, address, city..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Box>

            {/* Branch List */}
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
                overscrollBehavior: 'contain',
                pb: 1.5,
                '&::-webkit-scrollbar': {
                  width: 8,
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0,0,0,0.2)',
                  borderRadius: 999,
                },
              }}
            >
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4, px: 3 }}>
                  <CircularProgress size={40} />
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                    Loading branches...
                  </Typography>
                </Box>
              ) : filteredBranches.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <LocationIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    {searchTerm ? 'No branches found' : 'No branches available'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {searchTerm ? 'Try adjusting your search terms' : 'Please contact your administrator'}
                  </Typography>
                </Box>
              ) : (
                <List
                  sx={{
                    py: 0,
                    pb: 1,
                    '& .MuiListItem-root': {
                      width: '100%',
                    },
                    '& .MuiListItemText-root': {
                      minWidth: 0,
                      margin: 0,
                    },
                    '& .MuiListItemText-primary': {
                      minWidth: 0,
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                    },
                    '& .MuiListItemText-secondary': {
                      minWidth: 0,
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                    },
                  }}
                >
                  {filteredBranches.map((branch) => (
                    <ListItem key={branch.id} disablePadding>
                      <ListItemButton
                        onClick={() => handleBranchSelect(branch)}
                        selected={selectedBranch?.id === branch.id}
                        sx={{
                          py: 1.5,
                          px: { xs: 2, sm: 3 },
                          alignItems: 'flex-start',
                          minHeight: 0,
                          '&.Mui-selected': {
                            backgroundColor: 'primary.main',
                            color: 'primary.contrastText',
                            '&:hover': {
                              backgroundColor: 'primary.dark',
                            },
                          },
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
                              {branch.isAllOutlets ? (
                                <BusinessIcon sx={{ fontSize: 20, flexShrink: 0 }} />
                              ) : (
                                <StoreIcon sx={{ fontSize: 20, flexShrink: 0 }} />
                              )}
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: 500,
                                  minWidth: 0,
                                  wordBreak: 'break-word',
                                  overflowWrap: 'anywhere',
                                  flex: 1,
                                }}
                              >
                                {branch.name}
                              </Typography>
                              {selectedBranch?.id === branch.id && (
                                <Chip
                                  label="Selected"
                                  size="small"
                                  color="success"
                                  sx={{ fontSize: '0.7rem', height: 20, flexShrink: 0 }}
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 1, minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: 0.5,
                                  minWidth: 0,
                                  wordBreak: 'break-word',
                                  overflowWrap: 'anywhere',
                                }}
                              >
                                <LocationIcon sx={{ fontSize: 14, mt: '2px', flexShrink: 0 }} />
                                <Box component="span" sx={{ display: 'block', minWidth: 0, flex: 1 }}>
                                  {branch.address}
                                </Box>
                              </Typography>
                              {(branch.city || branch.state) && (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                  {[branch.city, branch.state].filter(Boolean).join(', ')}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Paper>
        </Fade>
      </Modal>
    </>
  );
};

export default BranchSelector;